import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef } from '@angular/core';

@Component({
  selector: 'app-cart-detail-dialog',
  templateUrl: './cart-detail-dialog.component.html',
  styles: [`
    @media (max-width: 639px) {
      :host ::ng-deep .cart-detail-modal {
        width: 95vw !important;
        max-height: 80vh !important;
      }
    }
  `]
})
export class CartDetailDialogComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() item: any = null;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('detailOverlay') detailOverlay!: ElementRef;
  @ViewChild('detailWrapper') detailWrapper!: ElementRef;
  @ViewChild('frameTabScroll') frameTabScrollRef!: ElementRef;
  private movedToBody = false;
  activeFrameTab = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (this.isOpen) {
        document.body.style.overflow = 'hidden';
        setTimeout(() => this.moveToBody(), 0);
      } else {
        document.body.style.overflow = '';
        this.removeFromBody();
      }
    }
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
    this.removeFromBody();
  }

  private moveToBody() {
    if (this.movedToBody) return;
    const overlay = this.detailOverlay?.nativeElement;
    const wrapper = this.detailWrapper?.nativeElement;
    if (overlay) document.body.appendChild(overlay);
    if (wrapper) document.body.appendChild(wrapper);
    this.movedToBody = true;
  }

  private removeFromBody() {
    if (!this.movedToBody) return;
    const overlay = this.detailOverlay?.nativeElement;
    const wrapper = this.detailWrapper?.nativeElement;
    if (overlay?.parentNode === document.body) document.body.removeChild(overlay);
    if (wrapper?.parentNode === document.body) document.body.removeChild(wrapper);
    this.movedToBody = false;
  }

  close() {
    document.body.style.overflow = '';
    this.removeFromBody();
    this.closed.emit();
  }

  // Collage listing price (same as PDP and checkout)
  private get cfp(): number {
    return this.item?.collage_final_price || 0;
  }

  private get pd(): any {
    return this.item?.pricing_data || {};
  }

  private get qty(): number {
    return this.item?.quantity || 1;
  }

  // Tax-exclusive convention: Subtotal − Discount + GST = Estimated Total.
  private taxFactor(): number {
    const rate = Number(this.pd.tax_rate) || 18;
    return 1 + rate / 100;
  }

  // Product Price — base MRP (without GST)
  get rollPrice(): number {
    if (this.cfp > 0) return Number((this.cfp / this.taxFactor()).toFixed(2));
    if (this.pd.total) return this.pd.total;
    if (this.pd.total_with_tax) return Number((this.pd.total_with_tax / this.taxFactor()).toFixed(2));
    if (this.pd.amount_payable) return Number((this.pd.amount_payable / this.taxFactor()).toFixed(2));
    if (this.item?.calculated_total) return Number((this.item.calculated_total / this.taxFactor()).toFixed(2));
    return this.item?.price || 0;
  }

  // Subtotal = taxable amount (Product Price × Qty − Discount).
  // Renders below the Discount row so it represents the post-discount base.
  get subtotal(): number {
    return Number((this.rollPrice * this.qty - this.discountAmount).toFixed(2));
  }

  // Discount
  get discountRate(): number {
    if (this.cfp > 0) return 15;
    return this.pd.discount_rate || this.item?.discount_rate || 15;
  }

  // Base discount (without GST) — matches base Product Price above.
  // Don't reference this.subtotal here — it now depends on discountAmount.
  get discountAmount(): number {
    if (this.cfp > 0) {
      const baseCfp = this.cfp / this.taxFactor();
      return Number((baseCfp * 0.15 * this.qty).toFixed(2));
    }
    if (this.pd.discount) return this.pd.discount * this.qty;
    if (this.pd.discount_with_tax) return Number((this.pd.discount_with_tax / this.taxFactor()).toFixed(2)) * this.qty;
    return this.rollPrice * this.qty * this.discountRate / 100;
  }

  // Gift wrapping
  get giftWrapAmount(): number {
    return (this.pd.gift_wrap || 0) * this.qty;
  }

  get hasGiftWrap(): boolean {
    return this.giftWrapAmount > 0;
  }

  // Installation
  get installationAmount(): number {
    return (this.pd.installation_amount || 0);
  }

  get hasInstallation(): boolean {
    return this.installationAmount > 0;
  }

  // Tax
  get taxRate(): number {
    if (this.cfp > 0) return 18;
    return this.pd.tax_rate || 12;
  }

  get taxAmount(): number {
    if (this.cfp > 0) {
      // 18% of selling price (cfp * 0.85)
      return Number((this.cfp * 0.85 * 0.18 * this.qty).toFixed(2));
    }
    const totalTax = this.pd.total_tax || this.pd.tax || 0;
    const giftTax = this.pd.gift_tax || 0;
    const installTax = this.pd.installation_tax || 0;
    if (totalTax || giftTax || installTax) {
      return (totalTax + giftTax) * this.qty + installTax;
    }
    const base = this.item?.price || 0;
    return base * 0.12 * this.qty;
  }

  // Estimated Total
  // Collage: selling price (cfp * 0.85) — same as checkout
  // Others: amount_payable × qty + installation
  get estimatedTotal(): number {
    if (this.cfp > 0) return Number((this.cfp * 0.85 * this.qty).toFixed(2));
    if (this.pd.amount_payable) {
      return (this.pd.amount_payable * this.qty)
        + (this.pd.installation_amount || 0)
        + (this.pd.installation_tax || 0);
    }
    if (this.item?.calculated_total) return this.item.calculated_total * this.qty;
    return this.subtotal - this.discountAmount + this.taxAmount;
  }

  get sizeLabel(): string {
    if (this.item?.size_label) return this.item.size_label;
    if (this.item?.standard_size) return this.item.standard_size.label;
    return '';
  }

  get colorDisplay(): string {
    if (this.item?.max_color && this.item?.min_color) return `${this.item.max_color} and ${this.item.min_color}`;
    return this.item?.max_color || this.item?.color || '';
  }

  // Collage set helpers
  get isCollageSet(): boolean {
    return this.item?.product_id === 2 || this.item?.collage_layout_id > 0 ||
      (this.item?.wallpaper_type || '').toUpperCase().includes('COLLAGE');
  }

  get isWallArt(): boolean {
    return !!(this.item?.on_sale);
  }

  get productTypeLabel(): string {
    if (this.isCollageSet) return 'Print & Frame';
    if (this.isWallArt) return 'Wall Art';
    return 'Wall Paper';
  }

  get collageFrames(): any[] {
    return this.item?.customizedProductItems || [];
  }

  getFrameImageId(frame: any): string {
    let imgDetail = frame.image_detail || {};
    if (typeof imgDetail === 'string') { try { imgDetail = JSON.parse(imgDetail); } catch { imgDetail = {}; } }
    if (imgDetail.image_path) {
      const parts = imgDetail.image_path.split('/');
      return parts[parts.length - 1] || 'N/A';
    }
    return imgDetail.image_id || 'N/A';
  }

  scrollFrameTabs(dir: number) {
    if (this.frameTabScrollRef?.nativeElement) {
      this.frameTabScrollRef.nativeElement.scrollBy({ left: dir * 150, behavior: 'smooth' });
    }
  }
}
